<template>
  <div class="repurchase-page" v-loading="loading">
    <el-form size="small" inline>
      <el-form-item label="统计时间">
        <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd" unlink-panels range-separator="至"
          start-placeholder="开始日期" end-placeholder="结束日期" :clearable="false" @change="query" />
      </el-form-item>
      <el-form-item label="窗口">
        <el-radio-group v-model="days" @change="query">
          <el-radio-button :label="30">30 天</el-radio-button>
          <el-radio-button :label="90">90 天</el-radio-button>
          <el-radio-button :label="180">180 天</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="路线">
        <el-select v-model="pathId" style="width: 180px" @change="query">
          <el-option v-for="item in pathOpts" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
    </el-form>

    <p class="tip">只统计每个窗口开始前已经成交过的乘客。下单和成交都按乘车日，下单含未支付和已取消，按订单笔数计。窗口从开始日期往后切，最后不满一段标为未完结，不和上一窗口比。选了路线时，历史成交和这段订单都只算该路线。</p>

    <el-row :gutter="12" class="cards">
      <el-col :span="6">
        <el-card shadow="never">
          <div class="num">{{ formatAvg(headline.avgOrder) }}</div>
          <div class="label">{{ headline.period || "最近完整窗口" }} 人均下单</div>
          <div class="change" v-if="headline.shortRange">范围还不满一个窗口</div>
          <div class="change" v-else>较上一窗口 <span :class="changeClass(change.avgOrder)">{{ formatChange(change.avgOrder) }}</span></div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="never">
          <div class="num">{{ formatAvg(headline.avgFinish) }}</div>
          <div class="label">人均成交</div>
          <div class="change" v-if="headline.shortRange">范围还不满一个窗口</div>
          <div class="change" v-else>较上一窗口 <span :class="changeClass(change.avgFinish)">{{ formatChange(change.avgFinish) }}</span></div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="never">
          <div class="num">{{ formatRate(headline.repurchaseRate) }}</div>
          <div class="label">复购率</div>
          <div class="change" v-if="headline.shortRange">范围还不满一个窗口</div>
          <div class="change" v-else>较上一窗口 <span :class="changeClass(change.repurchaseRate)">{{ formatChange(change.repurchaseRate) }}</span></div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="never">
          <div class="num">{{ formatRate(headline.finishReturnRate) }}</div>
          <div class="label">成交回访率</div>
          <div class="change" v-if="headline.shortRange">范围还不满一个窗口</div>
          <div class="change" v-else>较上一窗口 <span :class="changeClass(change.finishReturnRate)">{{ formatChange(change.finishReturnRate) }}</span></div>
        </el-card>
      </el-col>
    </el-row>

    <div class="block-title">老用户下单和成交有没有变勤</div>
    <chart ref="rateChart" :height="360" />

    <div class="block-title">回来的人数和笔数</div>
    <chart ref="volumeChart" :height="320" />

    <div class="block-title">各窗口明细</div>
    <el-table :data="windows" stripe :row-class-name="rowClass" max-height="480" empty-text="这段时间没有老用户订单">
      <el-table-column prop="period" label="窗口" min-width="220" fixed />
      <el-table-column label="状态" width="90">
        <template slot-scope="scope">{{ scope.row.partial ? "未完结" : "完整" }}</template>
      </el-table-column>
      <el-table-column prop="oldBase" label="老用户基数" width="120" />
      <el-table-column prop="orderUsers" label="下单人数" width="110" />
      <el-table-column prop="orderCount" label="下单笔数" width="110" />
      <el-table-column label="人均下单" width="110">
        <template slot-scope="scope">{{ formatAvg(scope.row.avgOrder) }}</template>
      </el-table-column>
      <el-table-column prop="finishUsers" label="成交人数" width="110" />
      <el-table-column prop="finishCount" label="成交笔数" width="110" />
      <el-table-column label="人均成交" width="110">
        <template slot-scope="scope">{{ formatAvg(scope.row.avgFinish) }}</template>
      </el-table-column>
      <el-table-column label="复购率" width="100">
        <template slot-scope="scope">{{ formatRate(scope.row.repurchaseRate) }}</template>
      </el-table-column>
      <el-table-column label="下单回访率" width="120">
        <template slot-scope="scope">{{ formatRate(scope.row.orderReturnRate) }}</template>
      </el-table-column>
      <el-table-column label="成交回访率" width="120">
        <template slot-scope="scope">{{ formatRate(scope.row.finishReturnRate) }}</template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getOldUserRepurchase } from "@/api/data";
import { DateUtil } from "@/util";

function recentDays(days) {
  const end = new Date();
  const start = new Date(end.getTime());
  start.setDate(start.getDate() - (days - 1));
  return [DateUtil.format(start, "yyyy-MM-dd"), DateUtil.format(end, "yyyy-MM-dd")];
}

export default {
  computed: {
    ...mapState({
      pathOpts: (state) => [{ name: "全部路线", id: "" }, ...(state.pathList || [])]
    }),
    change() {
      return this.headline.change || {};
    }
  },
  data() {
    return {
      dateRange: recentDays(360),
      days: 30,
      pathId: "",
      loading: false,
      headline: {},
      windows: []
    };
  },
  created() {
    this.query();
  },
  methods: {
    formatAvg(value) {
      if (value == null || value === "") {
        return "—";
      }
      return Number(value).toFixed(2);
    },
    formatRate(value) {
      if (value == null || value === "") {
        return "—";
      }
      return `${Number(value).toFixed(1)}%`;
    },
    formatChange(value) {
      if (value == null || value === "") {
        return "—";
      }
      const num = Number(value);
      const sign = num > 0 ? "+" : "";
      return `${sign}${num.toFixed(1)}%`;
    },
    changeClass(value) {
      if (value == null || value === "") {
        return "";
      }
      const num = Number(value);
      if (num > 0) {
        return "up";
      }
      if (num < 0) {
        return "down";
      }
      return "";
    },
    rowClass({ row }) {
      return row.partial ? "window-partial" : "";
    },
    query() {
      if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
        this.$message.error("日期不能为空");
        return;
      }
      if (this.dateRange[0] > this.dateRange[1]) {
        this.$message.error("起始日期不能大于截止日期");
        return;
      }
      const param = {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        days: this.days
      };
      if (this.pathId) {
        param.pathId = this.pathId;
      }
      this.loading = true;
      getOldUserRepurchase(param).then((res) => {
        const data = res.data || {};
        this.headline = data.headline || {};
        this.windows = data.windows || [];
        this.renderCharts(this.windows);
      }).finally(() => {
        this.loading = false;
      });
    },
    renderCharts(windows) {
      const labels = windows.map((item) => (item.partial ? `${item.period} 未完结` : item.period));
      const rateOption = {
        tooltip: {
          trigger: "axis",
          formatter(items) {
            const lines = [items[0].axisValue];
            items.forEach((item) => {
              if (item.data == null) {
                lines.push(`${item.marker}${item.seriesName}：—`);
                return;
              }
              const suffix = item.seriesName.indexOf("率") >= 0 ? "%" : "";
              lines.push(`${item.marker}${item.seriesName}：${item.data}${suffix}`);
            });
            return lines.join("<br/>");
          }
        },
        legend: { data: ["人均下单次数", "人均成交次数", "复购率", "下单回访率", "成交回访率"] },
        grid: { left: 48, right: 56, bottom: 72, top: 48 },
        xAxis: { type: "category", data: labels, axisLabel: { rotate: 30 } },
        yAxis: [
          { type: "value", name: "人均次数" },
          { type: "value", name: "比例", max: 100, axisLabel: { formatter: "{value}%" } }
        ],
        series: [
          { name: "人均下单次数", type: "line", data: windows.map((item) => item.avgOrder == null ? null : item.avgOrder) },
          { name: "人均成交次数", type: "line", data: windows.map((item) => item.avgFinish == null ? null : item.avgFinish) },
          { name: "复购率", type: "line", yAxisIndex: 1, data: windows.map((item) => item.repurchaseRate == null ? null : item.repurchaseRate) },
          { name: "下单回访率", type: "line", yAxisIndex: 1, data: windows.map((item) => item.orderReturnRate == null ? null : item.orderReturnRate) },
          { name: "成交回访率", type: "line", yAxisIndex: 1, data: windows.map((item) => item.finishReturnRate == null ? null : item.finishReturnRate) }
        ]
      };
      const volumeOption = {
        tooltip: { trigger: "axis" },
        legend: { data: ["下单人数", "成交人数", "下单笔数", "成交笔数"] },
        grid: { left: 48, right: 24, bottom: 72, top: 48 },
        xAxis: { type: "category", data: labels, axisLabel: { rotate: 30 } },
        yAxis: { type: "value", name: "人数 / 笔数" },
        series: [
          { name: "下单人数", type: "bar", data: windows.map((item) => item.orderUsers) },
          { name: "成交人数", type: "bar", data: windows.map((item) => item.finishUsers) },
          { name: "下单笔数", type: "line", data: windows.map((item) => item.orderCount) },
          { name: "成交笔数", type: "line", data: windows.map((item) => item.finishCount) }
        ]
      };
      this.$nextTick(() => {
        if (this.$refs.rateChart) {
          this.$refs.rateChart.setOption(rateOption, true);
        }
        if (this.$refs.volumeChart) {
          this.$refs.volumeChart.setOption(volumeOption, true);
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.tip {
  color: #909399;
  font-size: 13px;
  line-height: 22px;
  margin: 0 0 12px;
}

.cards {
  margin-bottom: 8px;
}

.num {
  font-size: 22px;
  font-weight: 600;
  line-height: 32px;
}

.label {
  color: #606266;
  font-size: 13px;
}

.change {
  color: #909399;
  font-size: 12px;
  margin-top: 6px;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}

.block-title {
  margin: 18px 0 8px;
  font-size: 15px;
  font-weight: 600;
}

.repurchase-page /deep/ .window-partial td {
  color: #c0c4cc;
}
</style>
